import { Badge, Button, Input } from '@staff-app/ui';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { formatDayCentral } from '@/lib/format-date';
import {
  CustomerDocument,
  DOC_TYPE_LABEL,
  documentsApi,
} from '@/features/customers/lib/documents-api';
import { FileText } from 'lucide-react';
import { InvoiceLinkCell } from '../components/InvoiceLinkCell';
import { CustomerFacts } from '@/features/customers/components/CustomerFacts';
import { accountStatus } from '@/features/customers/lib/customer-info';
import { useStaffBillingSubscriptionStatus } from '@/features/customers/hooks/useStaffCustomer';

interface Match {
  accountId: string;
  companyName: string | null;
  /** Identity facts, batched server-side from the same `companies` read as the search. */
  ownerEmail: string | null;
  plan: string | null;
  planFeature: string | null;
  isTrial: boolean | null;
  isActive: boolean | null;
  stripeId: string | null;
  pairedScreens: number | null;
  maxScreens: number | null;
  documents: CustomerDocument[];
}

/** A landing-list row: one account, its most recent PO/contract, and how big the deal was. */
interface TopRow {
  accountId: string;
  companyName: string | null;
  ownerEmail: string | null;
  stripeId: string | null;
  maxScreens: number | null;
  document: CustomerDocument | null;
}

interface SearchResult {
  resolvedAs: 'stripeId' | 'accountId' | 'email' | 'domain';
  term: string;
  /** Accounts looked at, which is far more than are shown for a domain sweep. */
  scanned: number;
  accountsWithDocuments: number;
  truncated: boolean;
  matches: Match[];
}

/** Says what the server actually matched on, so a widened search is never silent. */
const RESOLVED_LABEL: Record<SearchResult['resolvedAs'], (t: string) => string> = {
  stripeId: (t) => `Stripe customer ${t}`,
  accountId: (t) => `Account id ${t}`,
  email: (t) => `Email ${t}`,
  // Reached either by typing a bare domain, or when a full address matched nobody exactly
  // and the search widened — the count next to this makes the widening obvious.
  domain: (t) => `Email domain of ${t} — every account at that domain`,
};

/**
 * Document Center — the cross-customer entry point.
 *
 * Deliberately NOT a browse-everything list. You arrive knowing a customer and needing
 * their paperwork, so the page opens on a single search field and shows nothing until you
 * identify someone. That also keeps the access ledger meaningful: every result set is tied
 * to a deliberate lookup rather than an idle scroll through every contract we hold.
 */


/**
 * The upload dialog defaults Title to the filename minus its extension, so for most
 * documents the two are the same string and showing both just prints it twice. Show the
 * filename only when it genuinely differs — i.e. when someone gave the document a real
 * title ("Acme MSA 2026" for scan_001.pdf), which is exactly when it is worth seeing.
 */
const filenameAddsInfo = (title: string, filename: string | null): boolean => {
  if (!filename) return false;
  const stem = filename.replace(/\.[^.]+$/, '');
  return stem.trim().toLowerCase() !== title.trim().toLowerCase();
};

/**
 * The account header — the same identity card the customer pages show, rather than a bare id.
 *
 * `CustomerFacts` is reused verbatim (its own doc notes it renders in several places), so plan,
 * status, owner, ids and screen counts read identically wherever an operator meets them. Every
 * fact but one arrives with the search, already batched server-side.
 *
 * The exception is subscription status, which is a Stripe read. It is fetched HERE, per shown
 * account, through the shared cache-first hook the customer search already uses — and it skips
 * itself when the account has no Stripe id, so an unbilled account costs no call.
 *
 * Its own component because that hook cannot be called in a loop.
 */
/**
 * One account and everything we hold for it.
 *
 * Extracted so the SEARCH results and the LANDING list render through the same code. They show
 * the same thing and only differ in how the set was chosen, so two copies would only be a way
 * for one of them to fall behind.
 *
 * The handlers are props rather than closed over: each caller re-reads its own list after a
 * document is linked — the search re-runs the search, the landing list re-reads the top 20.
 */
function AccountBlock({
  match: m,
  onOpen,
  onSaved,
}: {
  match: Match;
  onOpen: (doc: CustomerDocument, disposition: 'inline' | 'attachment') => void;
  onSaved: () => void | Promise<void>;
}) {
  return (
              <div className="rounded-md border" data-testid={`doc-center-account-${m.accountId}`}>
                <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
                  <AccountFacts match={m} />
                  <Link
                    to={`/staff/customers/${encodeURIComponent(m.accountId)}/documents`}
                    className="text-sm underline underline-offset-4"
                    data-testid={`doc-center-open-${m.accountId}`}
                  >
                    {'Open customer →'}
                  </Link>
                </div>

                {m.documents.length === 0 ? (
                  // Distinct from "no such customer": we found them and hold nothing.
                  <p className="px-4 py-6 text-sm text-muted-foreground">
                    {'No documents on file for this customer.'}
                  </p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="text-muted-foreground">
                      <tr className="border-b">
                        <th className="whitespace-nowrap px-4 py-2 text-left font-medium">{'Upload date'}</th>
                        <th className="px-4 py-2 text-left font-medium">{'Title'}</th>
                        <th className="px-4 py-2 text-left font-medium">{'Type'}</th>
                        <th className="whitespace-nowrap px-4 py-2 text-left font-medium">{'Reference number'}</th>
                        <th className="px-4 py-2 text-left font-medium">{'Invoice ID'}</th>
                        <th className="whitespace-nowrap px-4 py-2 text-left font-medium">{'Actions'}</th>
                        <th className="px-4 py-2 text-left font-medium">{'Uploaded by'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {m.documents.map((doc) => (
                        <tr key={doc.id} className="border-b last:border-0" data-testid={`doc-center-row-${doc.id}`}>
                          <td className="whitespace-nowrap px-4 py-2 text-muted-foreground">
                            {formatDayCentral(doc.createdAt)}
                          </td>
                          {/*
                            One line each, ellipsis on overflow. The width bound sits on the
                            INNER div because an auto-layout table ignores max-width on a td,
                            and min-w-0 is required or the flex child refuses to shrink below
                            its content and never triggers the ellipsis.
                          */}
                          <td className="px-4 py-2">
                            <div className="flex max-w-[22rem] items-center gap-2">
                              <button
                                type="button"
                                onClick={() => void onOpen(doc, 'inline')}
                                title={doc.title}
                                className="min-w-0 cursor-pointer truncate text-left underline-offset-2 hover:underline"
                                data-testid={`doc-center-open-doc-${doc.id}`}
                              >
                                {doc.title}
                              </button>
                              {doc.legalHold ? (
                                <Badge variant="destructive" className="shrink-0">
                                  {'Legal hold'}
                                </Badge>
                              ) : null}
                            </div>
                            {filenameAddsInfo(doc.title, doc.currentFilename) ? (
                              <button
                                type="button"
                                onClick={() => void onOpen(doc, 'inline')}
                                title={doc.currentFilename ?? undefined}
                                className="block max-w-[22rem] cursor-pointer truncate text-left text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                                data-testid={`doc-center-filename-${doc.id}`}
                              >
                                {doc.currentFilename}
                              </button>
                            ) : null}
                          </td>
                          <td className="px-4 py-2">{DOC_TYPE_LABEL[doc.docType]}</td>
                          <td className="whitespace-nowrap px-4 py-2">{doc.externalNumber ?? '—'}</td>
                          <td className="px-4 py-2">
                            <InvoiceLinkCell doc={doc} prefix="doc-center" onSaved={onSaved} />
                          </td>
                          <td className="whitespace-nowrap px-4 py-2">
                            <div className="flex gap-1">
                              <Button size="sm" variant="outline" className="shrink-0" onClick={() => void onOpen(doc, 'inline')}>
                                {'Preview'}
                              </Button>
                              <Button size="sm" variant="outline" className="shrink-0" onClick={() => void onOpen(doc, 'attachment')}>
                                {'Download'}
                              </Button>
                            </div>
                          </td>
                          <td className="px-4 py-2 text-muted-foreground">{doc.createdBy}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
  );
}


function AccountFacts({ match }: { match: Match }) {
  const { data: sub } = useStaffBillingSubscriptionStatus(match.stripeId);
  const subStatus = sub?.staffBillingSubscriptionStatus;
  return (
    <div className="flex flex-col gap-0.5 text-xs text-muted-foreground">
      {/* Same rule as the Customers card: no name, no heading. CustomerFacts below already
          carries the account id, so falling back to it would just print it twice. */}
      {match.companyName ? (
        <div className="text-sm font-medium text-foreground">{match.companyName}</div>
      ) : null}
      <CustomerFacts
        prefix={`doc-center-${match.accountId}`}
        status={accountStatus(match.isTrial ?? undefined, match.isActive ?? undefined)}
        plan={match.plan}
        planFeature={match.planFeature}
        subscriptionStatus={subStatus?.status}
        subscriptionMultiple={subStatus?.multiple}
        ownerEmail={match.ownerEmail}
        accountId={match.accountId}
        stripeId={match.stripeId}
        pairedScreens={match.pairedScreens}
        maxScreens={match.maxScreens}
      />
    </div>
  );
}

/**
 * The screen change on a document, signed.
 *
 * A `+` prefix is NOT unconditional: the Change Screens card accepts a NEGATIVE delta — a
 * customer reducing screens can still send a PO for it — and `+${-5}` renders "+-5". Only a
 * genuine increase gets the plus.
 */
const signedScreens = (n: number): string => (n > 0 ? `+${n}` : String(n));

export function DocumentCenterPage() {
  const [term, setTerm] = useState('');
  const [result, setResult] = useState<SearchResult | null>(null);
  /** The landing list — the biggest accounts, shown until a search replaces it. */
  const [top, setTop] = useState<{ scanned: number; matches: TopRow[] } | null>(null);
  const [topLoading, setTopLoading] = useState(true);
  const [loading, setLoading] = useState(false);

  const search = async () => {
    const q = term.trim();
    if (q.length < 2) {
      toast.error('Enter at least 2 characters');
      return;
    }
    setLoading(true);
    try {
      const res = await documentsApi.customerSearch(q);
      setResult(res);
    } catch (e: unknown) {
      toast.error('Search failed', { description: (e as Error)?.message ?? 'Unknown error' });
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Load the landing list once, on mount.
   *
   * Deliberately NOT re-run when a search happens: the search replaces what is on screen, and
   * re-reading a list nobody is looking at would only add ledger rows. It reloads after a
   * document is linked FROM the list, because then the value on screen has changed.
   */
  const loadTop = async () => {
    setTopLoading(true);
    try {
      setTop(await documentsApi.topAccounts());
    } catch {
      // Silent: this is a convenience list on an otherwise empty page, and a failure here must
      // not look like the search is broken. The prompt to search stays either way.
      setTop(null);
    } finally {
      setTopLoading(false);
    }
  };

  useEffect(() => {
    void loadTop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const open = async (doc: CustomerDocument, disposition: 'inline' | 'attachment') => {
    try {
      const { url } = await documentsApi.download(doc.id, disposition);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e: unknown) {
      toast.error('Could not open document', {
        description: (e as Error)?.message ?? 'Unknown error',
      });
    }
  };

  const totalDocs = result?.matches.reduce((n, m) => n + m.documents.length, 0) ?? 0;

  return (
    // Container matches CustomersPage exactly (flex flex-col gap-6 p-6 + a gap-1 header
    // block) so the two pages line up when you move between them — the sidebar is shared,
    // so any difference in padding or heading weight reads as a bug.
    <div className="flex flex-col gap-6 p-6" data-testid="document-center-page">
      <div className="flex flex-col gap-1">
        <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground">
          <FileText className="h-6 w-6" />
          {'Document Center'}
        </h1>
        <p className="text-sm text-muted-foreground">
          {'Find a customer by email, domain, account id, or Stripe id, then see what we hold for them — contracts, tax-exemption certificates, POs.'}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void search();
          }}
          placeholder="Customer email, email domain, account id, or Stripe id (cus_…)"
          className="max-w-xl"
          data-testid="doc-center-input"
        />
        <Button onClick={() => void search()} disabled={loading} data-testid="doc-center-search">
          {loading ? 'Searching…' : 'Search'}
        </Button>
        {result ? (
          <Button
            variant="outline"
            onClick={() => {
              setTerm('');
              setResult(null);
            }}
            data-testid="doc-center-clear"
          >
            {'Clear'}
          </Button>
        ) : null}
      </div>

      {!result ? (
        <>
          <p className="text-xs text-muted-foreground" data-testid="doc-center-idle">
            {topLoading
              ? 'Loading the largest accounts…'
              : top && top.matches.length
                ? `${top.matches.length} most recent PO(s)/contract(s), ranked by the screens each deal added — unrecorded ones last. Search above for a specific customer.`
                : 'Enter a customer identifier above to begin.'}
          </p>
          {top && top.matches.length ? (
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm" data-testid="doc-center-top-table">
                <thead className="bg-muted/40 text-xs text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium">{'Customer'}</th>
                    <th className="px-4 py-2 text-left font-medium">{'Latest PO / Contract'}</th>
                    <th className="px-4 py-2 text-left font-medium">{'Type'}</th>
                    <th className="whitespace-nowrap px-4 py-2 text-left font-medium">{'Uploaded'}</th>
                    <th className="whitespace-nowrap px-4 py-2 text-right font-medium">
                      {'Screens added'}
                    </th>
                    <th className="whitespace-nowrap px-4 py-2 text-right font-medium">
                      {'Licensed'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {top.matches.map((m) => (
                    <tr
                      key={m.accountId}
                      className="border-t"
                      data-testid={`doc-center-top-row-${m.accountId}`}
                    >
                      <td className="px-4 py-2">
                        <Link
                          to={`/staff/customers/${encodeURIComponent(m.accountId)}/documents`}
                          className="underline underline-offset-4"
                        >
                          {m.companyName ?? m.ownerEmail ?? m.accountId}
                        </Link>
                      </td>
                      <td className="px-4 py-2">
                        {m.document ? (
                          <button
                            type="button"
                            className="max-w-[26rem] truncate text-left underline-offset-2 hover:underline"
                            onClick={() => m.document && void open(m.document, 'inline')}
                            data-testid={`doc-center-top-doc-${m.accountId}`}
                          >
                            {m.document.title}
                          </button>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-2">
                        {m.document ? DOC_TYPE_LABEL[m.document.docType] : '—'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-muted-foreground">
                        {m.document ? formatDayCentral(m.document.createdAt) : '—'}
                      </td>
                      {/*
                        The screen increase is only known at the moment the Billing Console runs,
                        so it is stamped onto the document there. Anything uploaded before that —
                        or outside a billing card — has no number to show, and an em dash is the
                        honest answer rather than repeating the licensed total as if it were the
                        increase.
                      */}
                      <td className="whitespace-nowrap px-4 py-2 text-right font-medium">
                        {typeof m.document?.meta?.screens === 'number'
                          ? signedScreens(m.document.meta.screens)
                          : '—'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-right text-muted-foreground">
                        {m.maxScreens ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </>
      ) : (
        <>
          <p className="text-xs text-muted-foreground" data-testid="doc-center-resolved">
            {`Matched on: ${RESOLVED_LABEL[result.resolvedAs](result.term)} · ${result.scanned} account(s) searched · ${result.accountsWithDocuments} with documents · ${totalDocs} document(s)`}
            {result.truncated
              ? ` · showing ${result.matches.length}, documents first`
              : ''}
          </p>

          {result.matches.length === 0 ? (
            <div
              className="rounded-md border border-dashed p-10 text-center text-sm text-muted-foreground"
              data-testid="doc-center-no-customer"
            >
              {'No customer matched that identifier.'}
            </div>
          ) : (
            result.matches.map((m) => (
              <AccountBlock key={m.accountId} match={m} onOpen={open} onSaved={search} />
            ))
          )}
        </>
      )}
    </div>
  );
}
